import { Button } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { readCookieConsent, writeCookieConsent } from "@/lib/cookie-consent";
import { space } from "@/theme/foundation";
import type { CookieConsentStatus } from "@/types/cookie-consent";

/**
 * PDPA-style cookie notice. Shown once until the user makes a choice (stored
 * per browser, keyed to the Cookie Policy version). The app currently sets
 * only strictly-necessary cookies, so "necessary only" disables nothing today —
 * but the stored choice is the gate for any analytics/marketing trackers added
 * later (see `hasNonEssentialConsent`). Mounted once in RootLayout so it covers
 * both the public and in-app surfaces, inside router context for <Link>.
 */
export function CookieConsent() {
  const { t } = useTranslation();
  const [decided, setDecided] = useState(() => readCookieConsent() !== null);

  if (decided) return null;

  function choose(status: CookieConsentStatus) {
    writeCookieConsent(status);
    setDecided(true);
  }

  return (
    <div
      role="dialog"
      aria-label={t("common:cookies.dialogLabel")}
      className="card-elev"
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 1000,
        maxWidth: 460,
        marginInline: "auto",
        padding: space[4],
        border: "1px solid var(--hairline)",
      }}
    >
      <p className="t-bold-16" style={{ margin: "0 0 var(--space-1)" }}>
        {t("common:cookies.title")}
      </p>
      <p className="t-12-ash" style={{ margin: "0 0 var(--space-3)", lineHeight: 1.5 }}>
        {t("common:cookies.body")}{" "}
        <Link to="/legal/cookie-policy" className="text-primary" style={{ textDecoration: "underline" }}>
          {t("common:cookies.policyLink")}
        </Link>
      </p>
      <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
        <Button size="small" onClick={() => choose("necessary")}>
          {t("common:cookies.essentialOnly")}
        </Button>
        <Button size="small" type="primary" onClick={() => choose("accepted")}>
          {t("common:cookies.acceptAll")}
        </Button>
      </div>
    </div>
  );
}
